import React from 'react';
import clsx from 'clsx';
import styles from '../MagicSignTable.module.scss';
import { MagicSignRow, ConfigMeta, Signatory } from '../../../types';
import LoadingSpinner from '../../../../_custom/LoadingSpinner/LoadingSpinner';
import Checkbox from '../../../../_custom/Checkbox/Checkbox';
import { PURPLE_DROPDOWN_CARET } from '../../../../../assets/hostedassets';
import { MAGIC_SIGN_INTERNAL_TEAM_LABEL } from '../../../constants';
import useSignatoryInlineDropdown, { SectionUser } from './useSignatoryInlineDropdown';

interface SignatoryInlineDropdownProps {
    row: MagicSignRow;
    configMeta: ConfigMeta | null;
    onSave: (id: string, signatories: Signatory[]) => void;
}

const SignatoryInlineDropdown: React.FC<SignatoryInlineDropdownProps> = ({ row, configMeta, onSave }) => {
    const {
        open,
        search,
        companyUsers,
        pnoUsers,
        usersLoading,
        selected,
        containerRef,
        maxSignatories,
        isLocked,
        atCap,
        handleOpen,
        handleSearchChange,
        isChecked,
        handleToggle,
    } = useSignatoryInlineDropdown({ row, configMeta, onSave });

    // Renders one labelled section; returns null for an empty list so the section is omitted.
    const renderSection = (label: string, list: SectionUser[]) => {
        if (!list.length) return null;
        return (
            <div className={styles.SignatorySection}>
                <div className={styles.SignatorySectionHeader}>{label}</div>
                {list.map((user) => {
                    const checked = isChecked(user);
                    const disabled = isLocked || (!checked && atCap);
                    return (
                        <Checkbox
                            key={`${label}-${user.userId}-${user.email}`}
                            label={
                                <span className={styles.SignatoryOptionLabel}>
                                    <span className={styles.SignatoryOptionPrimary}>{user.name}</span>
                                    <span
                                        className={styles.SignatoryOptionSecondary}
                                        title={user.orgName ? `${user.email} · ${user.orgName}` : user.email}
                                    >
                                        {user.orgName ? `${user.email} · ${user.orgName}` : user.email}
                                    </span>
                                </span>
                            }
                            checked={checked}
                            disabled={disabled}
                            isMultiSelect
                            customStyles={{ padding: '0.5rem 0.75rem' }}
                            onChange={() => handleToggle(user)}
                        />
                    );
                })}
            </div>
        );
    };

    // ── Display summary (closed-state pill) ──────────────────────────────────
    const renderSummary = () => {
        const triggerClass = clsx(styles.SignatoryTrigger, {
            [styles.SignatoryTriggerSelected]: selected.length > 0,
            [styles.SignatoryTriggerLocked]: isLocked,
        });

        if (selected.length === 0) {
            return (
                <button
                    className={triggerClass}
                    onClick={handleOpen}
                    aria-label="Select signatory"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                >
                    <span className={clsx(styles.SignatoryTriggerText, styles.SignatoryTriggerPlaceholder)}>
                        {isLocked ? '—' : 'Select'}
                    </span>
                    <span className={styles.SignatoryTriggerChevron}>
                        <img src={PURPLE_DROPDOWN_CARET} alt="" height={9} width={9} />
                    </span>
                </button>
            );
        }

        const first = selected[0];
        const extra = selected.length - 1;
        return (
            <button
                className={triggerClass}
                onClick={handleOpen}
                aria-label="Edit signatories"
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={styles.SignatoryTriggerText} title={first.name}>
                    {first.name}
                </span>
                {extra > 0 && <span className={styles.SignatoryTriggerExtra}>+{extra}</span>}
                <span className={styles.SignatoryTriggerChevron}>
                    <img src={PURPLE_DROPDOWN_CARET} alt="" height={9} width={9} />
                </span>
            </button>
        );
    };

    const selectedAsUsers: SectionUser[] = selected.map((s) => ({
        userId: s.userId ?? '',
        name: s.name ?? '',
        email: s.email ?? '',
        orgName: s.orgName,
    }));

    // Selected users live only in the pinned "Selected" section, so drop them from their group.
    // Keyed on userId so two distinct users sharing an email stay independent.
    const selectedUserIds = new Set(selectedAsUsers.map((u) => u.userId));
    const companyUnselected = (companyUsers as SectionUser[]).filter((u) => !selectedUserIds.has(u.userId));
    const pnoUnselected = (pnoUsers as SectionUser[]).filter((u) => !selectedUserIds.has(u.userId));

    const sections: { label: string; items: SectionUser[] }[] = [
        { label: 'Selected', items: selectedAsUsers },
        { label: 'Company Users', items: companyUnselected },
        { label: MAGIC_SIGN_INTERNAL_TEAM_LABEL, items: pnoUnselected },
    ].filter((s) => s.items.length > 0);

    return (
        <div ref={containerRef} className={styles.SignatoryDropdownContainer}>
            {renderSummary()}
            {open && (
                <div className={styles.SignatoryDropdownPanel} role="group" aria-label="Select signatories">
                    <input
                        type="text"
                        className={styles.SignatorySearchInput}
                        placeholder="Search users..."
                        value={search}
                        onChange={handleSearchChange}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                    />
                    {atCap && !isLocked && <div className={styles.SignatoryCapBanner}>Maximum {maxSignatories} signatories reached.</div>}
                    {usersLoading ? (
                        <div className={styles.SignatoryLoaderRow}>
                            <LoadingSpinner height="1.25rem" color="var(--primary-text-colour)" />
                        </div>
                    ) : sections.length === 0 ? (
                        <div className={styles.SignatoryEmptyHint}>{search ? 'No users found.' : 'No users available.'}</div>
                    ) : (
                        sections.map((s, i) => (
                            <React.Fragment key={s.label}>
                                {i > 0 && <div className={styles.SignatorySectionDivider} />}
                                {renderSection(s.label, s.items)}
                            </React.Fragment>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default SignatoryInlineDropdown;
